import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { ShopTypes } from '~/lib/data-contract';
import { useTranslations } from '~/services/umbraco/hooks';
import { Button } from '~/shared/components/Button';
import { CheckBox, Input } from '~/shared/components/Form';
import { Text } from '~/shared/components/Text';
import { InputSection, Paragraph, StyledButton, StyledForm, StyledText } from './styled';
import CheckmarkIcon from '~/icons/iconography/checkmark.svg';

import useSetUsersList from '~/services/shop/hooks/mit-fog/useSetUsersList';
import { Validation } from '~/shared/utils/validation';

type Props = {
    handleClose: () => void;
    user: ShopTypes.CustomerUserItemDto | undefined;
    customerId?: string;
};

export default function EditUsers({ handleClose, user, customerId }: Props) {
    const { translate } = useTranslations();
    const { mutate: setUser, isLoading } = useSetUsersList();

    const methods = useForm<FieldValues>({
        mode: 'onTouched',
        reValidateMode: 'onChange',
        defaultValues: {
            email: user?.email,
            userName: user?.userName,
            phoneNumber: user?.phoneNumber,
            administrator: user?.roles?.includes(1),
            seeAccountInformationRole: user?.roles?.includes(2),
            seePricesRole: user?.roles?.includes(7),
            priceExportAllowed: user?.roles?.includes(5),
        },
    });

    const onSubmitHandler: SubmitHandler<FieldValues> = (data) => {
        const {
            administrator,
            phoneNumber,
            priceExportAllowed,
            seeAccountInformationRole,
            seePricesRole,
            userName,
        } = data;

        if (!user?.userId && !customerId) {
            return;
        }

        let rolesSet = new Set(user?.roles || []);
        administrator ? rolesSet.add(1) : rolesSet.delete(1);
        seeAccountInformationRole ? rolesSet.add(2) : rolesSet.delete(2);
        priceExportAllowed ? rolesSet.add(5) : rolesSet.delete(5);
        seePricesRole ? rolesSet.add(7) : rolesSet.delete(7);

        const updatedData: ShopTypes.EditUserRequest = {
            customerId: customerId || '',
            userId: user?.userId || '',
            userName: userName,
            phoneNumber: phoneNumber,
            roles: Array.from(rolesSet),
        };

        setUser(
            {
                users: updatedData,
            },
            {
                onSuccess: () => {
                    handleClose();
                },
            },
        );
    };
    const getRequiredText = (field: string) =>
        `${field} ${translate('checkout.requiredField.text')}`;

    return (
        <>
            <Text variant="display2">{translate('mit-fog.userList.editUser')}</Text>
            <StyledForm methods={methods} onSubmit={onSubmitHandler}>
                <InputSection>
                    <Input
                        name="email"
                        placeholder="E-mail"
                        required={getRequiredText('E-mail')}
                        type="email"
                        value={user?.email}
                        disabled={Boolean(user?.email)}
                    />
                    <Input
                        name="userName"
                        placeholder="Navn"
                        required={getRequiredText('Fornavn')}
                        type="text"
                    />
                    <Input
                        name="phoneNumber"
                        hideStar
                        pattern={{
                            value: Validation.PhoneNumber,
                            message: translate('checkout.validation.phoneNumber'),
                        }}
                        placeholder="Telefonnr."
                        type="tel"
                    />
                </InputSection>
                <div>
                    <StyledText variant="display5">
                        {translate('mit-fog.userList.adminRights')}
                    </StyledText>
                    <CheckBox name="seeAccountInformationRole">
                        <Paragraph as="span">
                            {translate('mit-fog.userList.viewAccountInfo')}
                        </Paragraph>
                    </CheckBox>
                    <CheckBox name="administrator">
                        <Paragraph as="span">
                            {translate('mit-fog.userList.manageUserRights')}
                        </Paragraph>
                    </CheckBox>
                    <CheckBox name="priceExportAllowed">
                        <Paragraph as="span">
                            {translate('mit-fog.userList.priceExportAllowed')}
                        </Paragraph>
                    </CheckBox>
                    <CheckBox name="seePricesRole">
                        <Paragraph as="span">
                            {translate('mit-fog.userList.viewPricesInfo')}
                        </Paragraph>
                    </CheckBox>
                </div>
                <Button
                    type="submit"
                    disabled={isLoading || !methods.formState.isValid}
                    customIcon={<CheckmarkIcon />}
                >
                    {translate('mit-fog.userProfile.userInformation.saveChanges')}
                </Button>
            </StyledForm>
            <StyledButton onClick={handleClose} fullWidth variant="stroke" noIcon>
                {translate('mit-fog.userProfile.userInformation.cancel')}
            </StyledButton>
        </>
    );
}
